import React, { useState, useEffect } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '../../services/api';
import ReportDateSelector, { DateRangeOption } from './ReportDateSelector';
import { useCurrency } from '../CurrencyContext';
import { CURRENCIES } from '../../constants';

const SubscriptionLifecycleReport: React.FC = () => {
  const [range, setRange] = useState<DateRangeOption>('90d');
  const [report, setReport] = useState<any>({
    lifecycleData: [],
    avgNewMonth: 0,
    avgChurnMonth: 0,
    netSubscriberGrowth: 0,
    estArrGrowth: 0,
    activeSubscribers: 0,
  });
  const { currencyCode, currencySymbol } = useCurrency();
  const activeRate = CURRENCIES.find(c => c.code === currencyCode)?.rate || 1;

  useEffect(() => {
    let isMounted = true;
    const fetchLifecycle = async () => {
      try {
        const data = await api.analytics.getLifecycle(range);
        if (isMounted && data) {
          setReport(data);
        }
      } catch (err) {
        console.error('Failed to load lifecycle data:', err);
      }
    };
    fetchLifecycle();
    return () => { isMounted = false; };
  }, [range]);

  return (
    <div id="subscription-lifecycle-report" className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm col-span-1 lg:col-span-2">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-black text-slate-900 mb-1">Subscription Health Lifecycle</h2>
          <p className="text-sm text-slate-500">Volumetric subscriber additions, retentions, and churned accounts.</p>
        </div>
        <div className="flex items-center gap-3">
          <ReportDateSelector value={range} onChange={setRange} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Avg. New / Month</p>
          <p className="text-xl font-black text-emerald-600">+{report.avgNewMonth}</p>
        </div>
        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Avg. Churn / Month</p>
          <p className="text-xl font-black text-red-600">{report.avgChurnMonth}</p>
        </div>
        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Net Subscriber Growth</p>
          <p className="text-xl font-black text-indigo-600">+{report.netSubscriberGrowth}</p>
        </div>
        <div className="p-4 bg-indigo-900 rounded-2xl shadow-md shadow-indigo-100">
          <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1">Est. ARR Growth</p>
          <p className="text-xl font-black text-white">{currencySymbol}{Math.floor(report.estArrGrowth * activeRate).toLocaleString()}</p>
        </div>
      </div>

      <div className="h-[340px]">
        {(!report.lifecycleData || report.lifecycleData.length === 0) ? (
          <div className="h-full flex items-center justify-center text-slate-400 text-sm">Loading lifecycle metrics...</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={report.lifecycleData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b', fontWeight: 600}} dy={10} />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#64748b'}} />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#64748b'}} tickFormatter={(v) => `${currencySymbol}${Math.floor((v * activeRate)/1000)}k`} />
              <Tooltip
                contentStyle={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.05)', padding: '12px' }}
                formatter={(value: number, name: string) => {
                  const converted = Math.floor(value * activeRate);
                  if (name.includes('MRR')) return [`${currencySymbol}${converted.toLocaleString()}`, name];
                  return [value.toLocaleString(), name];
                }}
              />
              <Legend verticalAlign="top" align="right" height={32} iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: 700 }} />
              <Bar yAxisId="left" dataKey="newSubs" name="New Subscriptions" fill="#10b981" radius={[4, 4, 0, 0]} barSize={26} />
              <Bar yAxisId="left" dataKey="churned" name="Cancellations" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={26} />
              <Line yAxisId="left" type="monotone" dataKey="active" name="Active Subscribers" stroke="#6366f1" strokeWidth={3} dot={{ r: 4, fill: '#6366f1' }} />
              <Line yAxisId="right" type="monotone" dataKey="mrr" name="MRR Velocity" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default SubscriptionLifecycleReport;
